import { useTranslation } from 'react-i18next';

import { lendingUrl, sideMenuLinks, telegramRef, vkRef } from '@/config/consts';
import { useCookieDialogOpen } from '@/hooks/use-cookie-dialog-open';
import { Link, useLocation } from '@tanstack/react-router';
import { AlignJustify, XIcon } from 'lucide-react';

import { useWhoami } from '@/features/auth/hooks/use-whoami-query';
import { useGetUserById } from '@/features/user/hooks/use-get-user-by-id';

import { IconButton } from '@/lib/shadcn/components/ui/icon-button/icon-button';
import { MainButton } from '@/lib/shadcn/components/ui/main-button/main-button';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/lib/shadcn/components/ui/sheet';
import { SquareButton } from '@/lib/shadcn/components/ui/square-button/square-button';
import { Stack } from '@/lib/shadcn/components/ui/stack/stack';
import { cn } from '@/lib/shadcn/lib/utils';

import { BonusChip } from '../../entities/bonus/bonus-chip';
import { useGetBonusesByUserId } from '../../features/bonus/use-get-bonuses-by-user-id';
import { Profile } from '../auth-icon/profile';
import { Telegram } from '../contacts-icons/telegram';
import { Vkontakte } from '../contacts-icons/vkontakte';
import { Logo } from '../logo/logo';
import { Typography } from '../typography/typography';

interface SideMenuProps {
  trigger?: React.ReactNode;
  triggerClassName?: string;
}

export function SideMenu({ trigger, triggerClassName }: SideMenuProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const openCookieDialog = useCookieDialogOpen((s) => s.open);

  const { data: whoamiData } = useWhoami({
    params: {},
  });

  const userId = whoamiData?.auth.userId ?? '';
  const roles = whoamiData?.auth.roles ?? [];
  const isUnconfirmedClient = roles.includes('unconfirmed-client');
  const canAccessBackoffice =
    roles.includes('admin') || roles.includes('service_manager');

  const { data: userData } = useGetUserById({
    params: { userId },
    enabled: !!userId,
  });
  const {
    data: bonusData,
    isLoading: isBonusLoading,
    isError: isBonusError,
  } = useGetBonusesByUserId({
    params: {},
    enabled: !!userId,
  });

  return (
    <Sheet>
      <SheetTrigger asChild>
        {trigger ?? (
          <SquareButton className={triggerClassName} data-testid="menu-button">
            <AlignJustify className="text-slate-500" />
          </SquareButton>
        )}
      </SheetTrigger>
      <SheetContent className="flex h-full flex-col">
        <SheetTitle className="sr-only">{t('side-menu.title')}</SheetTitle>
        <SheetDescription className="sr-only">
          {t('side-menu.description')}
        </SheetDescription>
        <SheetHeader className="w-full flex-row items-center justify-between">
          <a href={lendingUrl} className="shrink-0">
            <Logo className="h-unit-13 text-slate-500" />
          </a>
          <SheetClose asChild>
            <button
              type="button"
              aria-label={t('common.close', 'Закрыть')}
              className="flex size-11 shrink-0 items-center justify-center rounded-medium bg-slate-100 text-slate-500 transition-colors hover:bg-slate-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 active:bg-slate-250"
            >
              <XIcon className="size-6 text-slate-500" />
            </button>
          </SheetClose>
        </SheetHeader>
        {isUnconfirmedClient ? (
          <MainButton asChild size="s" className="mt-14 w-full">
            <Link to="/auth" state={{ returnTo: location.href }}>
              {t('side-menu.login')}
            </Link>
          </MainButton>
        ) : null}
        {userData?.user ? (
          <SheetClose asChild>
            <Link
              to="/profile"
              className="mt-[56px] flex max-w-[280px] flex-row items-center gap-unit-4 rounded-large p-unit-4"
            >
              <Stack className="flex size-12 shrink-0 items-center justify-center rounded-large bg-[#DFECEF]">
                <Profile className="size-7" />
              </Stack>
              <Stack gap={1}>
                <Typography.p2 className="hyphens-auto whitespace-pre-wrap break-words text-slate-500">
                  {userData.user.lastName} {userData.user.firstName}
                </Typography.p2>
                <BonusChip
                  bonuses={bonusData?.bonuses ?? 0}
                  isLoading={isBonusLoading || isBonusError}
                />
              </Stack>
            </Link>
          </SheetClose>
        ) : null}
        <Stack align="start" gap={3} className="mt-[20px] overflow-y-auto">
          {Object.entries(sideMenuLinks).map(([key, value]) => (
            <SheetClose key={key} asChild>
              <a
                href={value}
                className={cn(
                  'rounded-large p-unit-5 text-p2 text-slate-400 hover:text-slate-500',
                )}
              >
                {t(`side-menu.links.${key}`)}
              </a>
            </SheetClose>
          ))}
          <SheetClose asChild>
            <button
              type="button"
              onClick={openCookieDialog}
              className={cn(
                'w-full rounded-large p-unit-5 text-left text-p2 text-slate-400 hover:text-slate-500',
              )}
            >
              {t('cookie-dialog.menu-item')}
            </button>
          </SheetClose>
        </Stack>
        <Stack gap={4} className="mt-auto w-full pt-unit-4">
          {canAccessBackoffice ? (
            <MainButton
              asChild
              size="s"
              variant="outlined"
              className="w-full"
              data-testid="backoffice-button"
            >
              <a href="/backoffice/">{t('side-menu.backoffice')}</a>
            </MainButton>
          ) : null}
          <Stack direction="row" gap={4}>
            <IconButton asChild>
              <a href={vkRef} target="_blank" rel="noreferrer">
                <Vkontakte />
              </a>
            </IconButton>
            <IconButton asChild>
              <a href={telegramRef} target="_blank" rel="noreferrer">
                <Telegram />
              </a>
            </IconButton>
          </Stack>
        </Stack>
      </SheetContent>
    </Sheet>
  );
}
